import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/logo-optimized.webp";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const navItems = [
    { label: t("nav.home"), path: "/" },
    {
      label: t("nav.aboutUs"),
      children: [
        { label: t("nav.aboutNSI"), path: "/about/nsi" },
        { label: t("nav.ourTeam"), path: "/about/team" },
      ],
    },
    {
      label: t("nav.sez"),
      children: [
        { label: t("nav.concept"), path: "/sez/concept" },
        { label: t("nav.whyCentralAsia"), path: "/sez/why-central-asia" },
        { label: t("nav.investment"), path: "/sez/investment" },
      ],
    },
    {
      label: t("nav.development"),
      children: [
        { label: t("nav.dryPort"), path: "/development/dry-port" },
        { label: t("nav.manufacturing"), path: "/development/manufacturing" },
        { label: t("nav.financialCenter"), path: "/development/financial-center" },
        { label: t("nav.retail"), path: "/development/retail" },
        { label: t("nav.hospitality"), path: "/development/hospitality" },
        { label: t("nav.energy"), path: "/development/energy" },
        { label: t("nav.logistics"), path: "/development/logistics" },
        { label: t("nav.medical"), path: "/development/medical" },
        { label: t("nav.convention"), path: "/development/convention" },
        { label: t("nav.customs"), path: "/development/customs" },
      ],
    },
    { label: t("nav.gallery"), path: "/gallery" },
    { label: t("nav.news"), path: "/news" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-8 h-24">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={logo} alt="NewSilkWay" className="h-16" width={72} height={64} fetchPriority="high" />
        </Link>

        <nav className="hidden md:flex items-center gap-0.5">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button className="flex items-center gap-1 px-1.5 py-2 text-xs uppercase tracking-wider text-gray-800 hover:text-primary transition-colors font-body whitespace-nowrap font-medium">
                  {item.label}
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {openDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-1 min-w-[260px] bg-white border border-gray-100 rounded-md shadow-xl py-2 animate-fade-in">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className="block px-5 py-2.5 text-sm text-gray-500 hover:text-primary hover:bg-gray-50 transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.path!}
                className="px-1.5 py-2 text-xs uppercase tracking-wider text-gray-800 hover:text-primary transition-colors font-body whitespace-nowrap font-medium"
              >
                {item.label}
              </Link>
            )
          )}
          <LanguageSwitcher />
        </nav>

        <button
          className="md:hidden text-gray-700"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    className="flex items-center justify-between w-full py-3 text-sm uppercase tracking-wider text-gray-600"
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="pl-4 space-y-1 pb-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block py-2 text-sm text-gray-500 hover:text-primary"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.path!}
                  className="block py-3 text-sm uppercase tracking-wider text-gray-600 hover:text-gray-900"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-2 border-t border-gray-100">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
